// src/hooks/useRoiData.js - V2 API ONLY - FAIL FAST
// No fallback logic - if data is wrong, we throw explicit errors
import { useState, useEffect, useMemo, useCallback } from 'react';

const API_BASE = '/api/v2';

// Scenario multipliers
const SCENARIO_MULTIPLIERS = {
  conservative: 0.7,
  base: 1.0,
  aggressive: 1.3
};

/**
 * Validate that API response has required V2 structure
 * Throws explicit error if validation fails
 */
function validateApiResponse(data) {
  if (!data) {
    throw new Error('ROI API returned null/undefined data');
  }

  if (!data.metrics) {
    throw new Error('ROI API response missing required "metrics" field');
  }

  const required = ['roi_3_year', 'implementation_cost', 'annual_benefit', 'payback_months'];
  const missing = required.filter(field => data.metrics[field] === undefined);
  
  if (missing.length > 0) {
    throw new Error(`ROI API metrics missing required fields: ${missing.join(', ')}`);
  }

  if (!Array.isArray(data.assumptions)) {
    console.warn('ROI API response missing assumptions array');
  }

  return true;
}

/**
 * Calculate weighted confidence from assumptions
 */
function calculateConfidence(assumptions) {
  if (!assumptions?.length) return 0.75;
  
  const scores = assumptions
    .map(a => {
      const score = a.confidence_score || a.confidence || 0;
      return score > 1 ? score / 100 : score;
    })
    .filter(s => s > 0);
  
  if (!scores.length) return 0.75;
  
  const avg = scores.reduce((sum, s) => sum + s, 0) / scores.length;
  return Math.min(0.95, avg);
}

/**
 * Compute headline metrics for a scenario
 * Uses V2 API metrics directly with scenario multiplier
 */
function computeHeadline(apiMetrics, scenario, assumptions) {
  const multiplier = SCENARIO_MULTIPLIERS[scenario];
  
  // Apply scenario multiplier to BENEFITS only (investment is constant)
  const benefitY1 = (apiMetrics.annual_benefit || apiMetrics.year_1_benefit) * multiplier;
  const investmentY1 = apiMetrics.implementation_cost;
  const netY1 = benefitY1 - investmentY1;
  
  // ROI scales with scenario
  const baseRoi = apiMetrics.roi_3_year;
  const roiPct = scenario === 'base' ? baseRoi : (netY1 / investmentY1) * 100;
  
  return {
    roiPct: Math.max(-100, Math.min(2000, roiPct)),
    paybackMonths: apiMetrics.payback_months,
    benefitY1,
    investmentY1,
    netY1,
    confidence: calculateConfidence(assumptions),
    monthlyBenefit: benefitY1 / 12
  };
}

/**
 * Extract value drivers from API data
 * Creates driver breakdown for waterfall visualization
 */
function extractDrivers(apiMetrics, calculations, scenario) {
  const multiplier = SCENARIO_MULTIPLIERS[scenario];
  const drivers = [];
  
  // Primary: Use annual benefit as main driver
  const totalBenefit = (apiMetrics.annual_benefit || apiMetrics.year_1_benefit) * multiplier;
  
  if (totalBenefit > 0) {
    drivers.push({
      label: 'Total Annual Benefits',
      value: totalBenefit,
      color: 'green',
      category: 'Benefits'
    });
  }
  
  // If calculations provide breakdown, add those
  if (calculations?.length > 0) {
    const benefitCalcs = calculations.filter(c => 
      c.note?.toLowerCase().includes('benefit') && c.result > 0
    );
    
    benefitCalcs.forEach(calc => {
      drivers.push({
        label: calc.note || 'Additional Benefit',
        value: calc.result * multiplier,
        color: 'blue',
        category: 'Component'
      });
    });
  }
  
  return drivers;
}

/**
 * Build sensitivity analysis from drivers
 */
function buildSensitivity(drivers, investmentY1) {
  return drivers.slice(0, 3).map(driver => {
    const delta = driver.value * 0.1; // ±10% change
    const impactOnROI = investmentY1 > 0 ? 
      ((delta / investmentY1) * 100).toFixed(1) : 0;
    
    return {
      label: driver.label,
      baseline: driver.value,
      upside: driver.value * 1.1,
      downside: driver.value * 0.9,
      impactPercent: parseFloat(impactOnROI),
      effect: `±${Math.round(impactOnROI)}% ROI`
    };
  });
}

/**
 * Generate investment recommendation based on metrics
 */
function getRecommendation(roiPct, paybackMonths, confidence) {
  if (!roiPct || roiPct === 0) {
    return {
      status: 'pending',
      rationale: 'ROI data not available for analysis.'
    };
  }
  
  if (roiPct >= 150 && paybackMonths <= 12 && confidence >= 0.7) {
    return {
      status: 'proceed',
      rationale: `Strong ROI of ${Math.round(roiPct)}% with ${paybackMonths}-month payback supports immediate investment.`
    };
  }
  
  if (roiPct >= 80 && paybackMonths <= 18) {
    return {
      status: 'caution',
      rationale: `Moderate ROI of ${Math.round(roiPct)}% warrants consideration with risk mitigation.`
    };
  }
  
  return {
    status: 'reconsider',
    rationale: `ROI of ${Math.round(roiPct)}% suggests exploring alternatives or optimizing assumptions.`
  };
}

/**
 * Build complete model for a scenario
 */
function buildModel(rawData, scenario) {
  // Validate that we have required data
  if (!rawData?.metrics) {
    throw new Error('Cannot build model: missing metrics');
  }
  
  const metrics = computeHeadline(rawData.metrics, scenario, rawData.assumptions);
  const drivers = extractDrivers(rawData.metrics, rawData.calculations, scenario);
  const sensitivity = buildSensitivity(drivers, metrics.investmentY1);
  
  return {
    headline: {
      roiPct: metrics.roiPct,
      paybackMonths: metrics.paybackMonths,
      confidence: metrics.confidence
    },
    metrics,
    drivers,
    tables: {
      assumptions: rawData.assumptions || [],
      sources: rawData.sources || []
    },
    notes: {
      method: rawData.headline?.time_to_roi || 'Year 1 financial model',
      updatedAt: new Date().toISOString()
    },
    sensitivity
  };
}

/**
 * Main ROI data hook
 * Fetches and transforms V2 API data for ROI components
 */
export const useRoiData = (clientId, useCaseId) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeScenario, setActiveScenario] = useState('base');
  const [rawData, setRawData] = useState(null);

  // Fetch ROI data from API
  const fetchData = useCallback(async () => {
    if (!clientId || !useCaseId) {
      setLoading(false);
      setError('Missing required parameters: clientId and useCaseId');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const endpoint = `${API_BASE}/roi/${clientId}/${useCaseId}`;
      console.log(`[useRoiData] Fetching: ${endpoint}`);
      
      const response = await fetch(endpoint);
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`No ROI data found for client ${clientId}, use case ${useCaseId}`);
        }
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log('[useRoiData] API response:', result);
      
      // Validate response structure
      validateApiResponse(result);
      
      setRawData(result);
      setError(null);
      
    } catch (err) {
      console.error('[useRoiData] Error:', err);
      setError(err.message);
      setRawData(null);
    } finally {
      setLoading(false);
    }
  }, [clientId, useCaseId]);

  // Fetch on mount and when IDs change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Build comparison models for all scenarios
  const comparison = useMemo(() => {
    if (!rawData) return [];
    
    try {
      return ['conservative', 'base', 'aggressive'].map(scenario => ({
        scenario,
        multiplier: SCENARIO_MULTIPLIERS[scenario],
        model: buildModel(rawData, scenario)
      }));
    } catch (err) {
      console.error('[useRoiData] Error building comparison:', err);
      setError(err.message);
      return [];
    }
  }, [rawData]);

  // Get model for active scenario
  const model = useMemo(() => {
    if (!rawData) return null;
    
    try {
      return buildModel(rawData, activeScenario);
    } catch (err) {
      console.error('[useRoiData] Error building model:', err);
      setError(err.message);
      return null;
    }
  }, [rawData, activeScenario]);

  // Get recommendation for active scenario
  const recommendation = useMemo(() => {
    if (!model?.metrics) {
      return { status: 'pending', rationale: 'No ROI data available.' };
    }
    
    return getRecommendation(
      model.metrics.roiPct,
      model.metrics.paybackMonths,
      model.metrics.confidence
    );
  }, [model]);

  // Return hook interface
  return {
    // Primary state
    loading,
    error,
    activeScenario,
    setActiveScenario,
    
    // Computed data
    comparison,
    model,
    recommendation,
    
    // Direct access to raw API data
    data: rawData,
    assumptions: rawData?.assumptions || [],
    sources: rawData?.sources || [],
    projections: rawData?.projections || [],
    calculations: rawData?.calculations || [],
    narrative: rawData?.narrative,
    
    // Convenience accessors
    metrics: model?.metrics,
    drivers: model?.drivers || [],
    sensitivity: model?.sensitivity || [],
    headline: model?.headline,
    
    // Legacy compatibility
    scenario: activeScenario,
    setScenario: setActiveScenario,
    
    // Utils
    refetch: fetchData
  };
};

export default useRoiData;